import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { AppState } from '@client/model/store';
import { fundingPolicyTypeLabel } from '../glossary/enumLabels';

export interface PolicyOption {
  fundingSourceId: string;
  label: string;
  carrierName?: string;
  hasProjection: boolean;
}

function policyDisplayLabel(
  policy: PolicyFundingSource,
  fundingSourceId: string,
): { label: string; carrierName?: string } {
  const sourceQuote = policy.sourceQuote;
  const carrierName =
    sourceQuote && typeof sourceQuote === 'object' && 'carrierName' in sourceQuote
      ? sourceQuote.carrierName
      : undefined;
  const customName = policy.policyCustomName ?? undefined;
  const typeLabel = fundingPolicyTypeLabel(policy.policyType as unknown as number);
  const label = customName ?? typeLabel ?? fundingSourceId;
  return { label, carrierName };
}

const selectClientDataOrNull = (state: AppState): Client | null => state.client.data;

export function usePolicyOptions(): PolicyOption[] {
  const client = useSelector(selectClientDataOrNull);
  return useMemo(() => {
    if (!client) return [];
    const projections = client.policyClaimProjections ?? {};
    const ltcPolicies = client.multipleFundingSources?.ltcPolicy ?? {};
    return Object.entries(ltcPolicies).map(([fundingSourceId, policy]) => {
      const { label, carrierName } = policyDisplayLabel(policy, fundingSourceId);
      return {
        fundingSourceId,
        label,
        carrierName,
        hasProjection: Boolean(projections[fundingSourceId]),
      };
    });
  }, [client]);
}

export function useProjectionForPolicy(policyId: string | undefined): ClaimProjection | null {
  const client = useSelector(selectClientDataOrNull);
  if (!client || !policyId) return null;
  return client.policyClaimProjections?.[policyId] ?? null;
}

export function usePolicyById(policyId: string | undefined): PolicyFundingSource | null {
  const client = useSelector(selectClientDataOrNull);
  if (!client || !policyId) return null;
  return client.multipleFundingSources?.ltcPolicy?.[policyId] ?? null;
}

export function useClientLoadStatus(): { loaded: boolean; clientFullName: string | undefined } {
  const client = useSelector(selectClientDataOrNull);
  return {
    loaded: client !== null,
    clientFullName: client?.clientFullName,
  };
}
